import {
  Apartment,
  Email,
  Home,
  LocalHospital,
  LocationCity,
  Lock,
  Phone,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import MessageDialog from "../../common/dialog/commonDialogBox";
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { styles } from "../../commonStyle/formStyles";
import { fetchOrganizationById, updateOrganization, resetCurrentOrganization } from "../../redux/organization/organizationAction.jsx";

const EditOrganization = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [messageDialog, setMessageDialog] = useState({
        open: false,
        title: "Notification",
        message: "",
        type: "success",
    });

    const orgData = useSelector((state) => state.organization?.currentOrganization?.data?.data);
    const orgState = useSelector((state) => state.organization?.updateOrganization || {});

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            org_name: "",
            hospital_name: "",
            email: "",
            phone: "",
            city: "",
            password: "",
            address: ""
        }
    });

    useEffect(() => {
        if (id) {
            dispatch(fetchOrganizationById(id));
        }
    }, [id, dispatch]);

    useEffect(() => {
        if (orgData) {
            setValue("org_name", orgData.org_name || "");
            setValue("hospital_name", orgData.hospital_name || "");
            setValue("email", orgData.email || "");
            setValue("phone", orgData.phone || "");
            setValue("city", orgData.city || "");
            setValue("address", orgData.address || "");
        }
    }, [orgData, setValue]);

    useEffect(() => {
        if (orgState.success) {
            setMessageDialog({
                open: true,
                title: "Organization Update",
                message: "Organization updated successfully!",
                type: "success",
            });
            dispatch(resetCurrentOrganization());
            setTimeout(() => {
                navigate(APP_LINK.LISTORGANIZATION);
            }, 1500);
        } else if (orgState.error) {
            setMessageDialog({
                open: true,
                title: "Update Error",
                message: orgState.error || "An error occurred while updating the organization",
                type: "error",
            });
            dispatch(resetCurrentOrganization());
        }
    }, [orgState, dispatch, navigate]);

    const inputFields = useMemo(() => [
        {
            name: "org_name",
            label: "Organization Name / நிறுவனத்தின் பெயர்",
            placeholder: "Enter organization name",
            type: "text",
            rules: { required: "Organization name is required" },
            startIcon: <Apartment />,
        },
        {
            name: "hospital_name",
            label: "Hospital Name / மருத்துவமனையின் பெயர்",
            placeholder: "Enter hospital name",
            type: "text",
            rules: { required: "Hospital name is required" },
            startIcon: <LocalHospital />,
        },
        {
            name: "email",
            label: "Email Address / மின்னஞ்சல் முகவரி",
            placeholder: "Enter email",
            type: "email",
            rules: { 
                required: "Email is required",
                pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                }
            },
            startIcon: <Email />,
        },
        {
            name: "phone",
            label: "Phone Number / தொலைபேசி எண்",
            placeholder: "Enter phone number",
            type: "tel",
            maxLength: 10,
            rules: { 
                required: "Phone number is required",
                pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Invalid phone number (10 digits required)"
                }
            },
            startIcon: <Phone />,
        },
        {
            name: "city",
            label: "City / மாநகரம்",
            placeholder: "Enter city",
            type: "text",
            rules: { required: "City is required" },
            startIcon: <LocationCity />,
        },
        {
            name: "password",
            label: "Password / கடவுச்சொல்",
            placeholder: "Leave blank to keep current",
            type: "password",
            startIcon: <Lock />,
        },
        {
            name: "address",
            label: "Address / முகவரி",
            placeholder: "Enter address",
            type: "text",
            multipleLine: true,
            rules: { required: "Address is required" },
            startIcon: <Home />,
        },
    ], []);

    const onSubmit = (data) => {
        // Only include password if it's not empty
        const submitData = { ...data };
        if (!submitData.password) {
            delete submitData.password;
        }
        dispatch(updateOrganization(id, submitData));
    };

    const handleBack = () => {
        navigate(APP_LINK.LISTORGANIZATION);
    };

    const handleDialogClose = () => {
        setMessageDialog({ ...messageDialog, open: false });
    };

    return (
        <Box sx={styles.container}>
            <Paper sx={styles.paper} elevation={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={8} lg={9}>
                        <Typography variant="h3" sx={styles.heading}>
                            Edit Organization
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={3} mb={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleBack}
                            startIcon={<ArrowBackIcon />}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <ReusableFormFields
                                inputFields={inputFields}
                                register={register}
                                errors={errors}
                                setValue={setValue}
                                control={control}
                                gridProps={{ xs: 12, sm: 12, md: 6 }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={styles.buttonsContainer}>
                        <CustomButton
                            type="button"
                            text="Cancel"
                            variant="contained"
                            disableElevation
                            size="small"
                            onClick={handleBack}
                        />
                        <CustomButton
                            type="submit"
                            text="Update Organization"
                            variant="contained"
                            disableElevation
                            size="small"
                            disabled={orgState.loading}
                            loading={orgState.loading}
                        />
                    </Box>
                </form>
            </Paper>
            <MessageDialog
                open={messageDialog.open}
                onClose={handleDialogClose}
                title={messageDialog.title}
                message={messageDialog.message}
                type={messageDialog.type}
            />
        </Box>
    );
};

export default EditOrganization;
